import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { FormErrors, useFormState } from '../../hooks/useFormState';
import { isBlank, isValidEmail } from '../../utils/validation';
import { buttonClass } from '../../utils/styles';
import { TextField } from '../form/TextField';
import { TextAreaField } from '../form/TextAreaField';
import { CheckboxField } from '../form/CheckboxField';
import { FormErrorSummary } from '../form/FormErrorSummary';
import { FormPreviewNotice } from '../form/FormPreviewNotice';
import { FormValidatedPanel } from '../form/FormValidatedPanel';
type EmployerValues = {
  name: string;
  email: string;
  company: string;
  role: string;
  requirement: string;
  consent: boolean;
};
const initialValues: EmployerValues = {
  name: '',
  email: '',
  company: '',
  role: '',
  requirement: '',
  consent: false
};
type EmployerInquiryFormProps = {
  formId?: string;
};
export function EmployerInquiryForm({
  formId = 'employer-form'
}: EmployerInquiryFormProps) {
  const {
    values,
    errors,
    status,
    fieldId,
    setField,
    handleSubmit,
    edit,
    reset
  } = useFormState(formId, initialValues, validate);
  if (status === 'validated') {
    return <FormValidatedPanel formId={formId} title="Your inquiry is complete" message={`Thank you, ${values.name.trim().split(' ')[0]}. Your hiring inquiry for the ${values.role.trim()} role at ${values.company.trim()} has passed all checks and is ready to send.`} onEdit={edit} onReset={reset} data-id="element-127" data-mp="16ec5u2c9m37r" />;
  }
  return <form noValidate onSubmit={handleSubmit} className="space-y-6" aria-label="Hiring inquiry" data-id="element-128" data-mp="1p3s9uzn5sdsx">
      {status === 'invalid' && <FormErrorSummary count={Object.keys(errors).length} data-id="element-129" data-mp="1u123krhkzbmk" />}
      <div className="grid gap-6 sm:grid-cols-2" data-id="element-130" data-mp="dod8ljnx2zs0">
        <TextField id={fieldId('name')} label="Full name" required autoComplete="name" value={values.name} onChange={v => setField('name', v)} error={errors.name} data-id="element-131" data-mp="dmiqse7xzns5" />
        <TextField id={fieldId('email')} label="Business email" type="email" required autoComplete="email" placeholder="name@company.com" value={values.email} onChange={v => setField('email', v)} error={errors.email} data-id="element-132" data-mp="3qp4norn7c1ri" />
        <TextField id={fieldId('company')} label="Company" required autoComplete="organization" value={values.company} onChange={v => setField('company', v)} error={errors.company} data-id="element-133" data-mp="3c08hl04pgytj" />
        <TextField id={fieldId('role')} label="Role to fill" required placeholder="e.g. Chief Financial Officer" value={values.role} onChange={v => setField('role', v)} error={errors.role} data-id="element-134" data-mp="3314w5pi2g7bv" />
      </div>
      <TextAreaField id={fieldId('requirement')} label="Brief description of the hiring requirement" required hint="Context, scope, location and timing are all helpful. Please avoid sharing confidential details at this stage." value={values.requirement} onChange={v => setField('requirement', v)} error={errors.requirement} data-id="element-135" data-mp="w6rdzpf2wm2q" />
      <CheckboxField id={fieldId('consent')} checked={values.consent} onChange={v => setField('consent', v)} error={errors.consent} data-id="element-136" data-mp="1iggaj2zka2sl">
        I have read the{' '}
        <Link to="/privacy-policy" className="font-medium text-navy underline underline-offset-2" data-id="element-137" data-mp="psu6xuqc2y5q">
          privacy notice
        </Link>{' '}
        and agree to Avenqor Talent Search using these details to respond to my inquiry.
      </CheckboxField>
      <div className="flex flex-col gap-5 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-138" data-mp="267lotdpusn0s">
        <FormPreviewNotice data-id="element-139" data-mp="2a749q9hocy3m" />
        <button type="submit" className={buttonClass('navy', 'shrink-0')} data-id="element-140" data-mp="2aaid9l22rdcn">
          Submit Inquiry
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-141" data-mp="28tco3d8lw0g9" />
        </button>
      </div>
    </form>;
}
function validate(v: EmployerValues): FormErrors<EmployerValues> {
  const e: FormErrors<EmployerValues> = {};
  if (isBlank(v.name)) e.name = 'Please enter your name.';
  if (isBlank(v.email)) e.email = 'Please enter your business email.';else if (!isValidEmail(v.email)) e.email = 'Please enter a valid email address, e.g. name@company.com.';
  if (isBlank(v.company)) e.company = 'Please enter your company name.';
  if (isBlank(v.role)) e.role = 'Please tell us which role you are looking to fill.';
  if (isBlank(v.requirement)) e.requirement = 'Please briefly describe the hiring requirement.';else if (v.requirement.trim().length < 20) e.requirement = 'Please add a little more detail (at least 20 characters).';
  if (!v.consent) e.consent = 'Please confirm you have read the privacy notice.';
  return e;
}